/* SneakClean Web 2.0 — single cart UI controller.
 * Uses locale-aware Shopify Ajax routes, line-item keys and bundled section
 * rendering. Product code may dispatch `sneakclean:cart-added`; this file only
 * refreshes/open the drawer for that event and never repeats the product add.
 */
(function () {
  "use strict";

  if (window.SneakCleanCartController) return;

  const SELECTORS = {
    shell: "[data-sc-cart-root]",
    drawer: "[data-sc-cart-drawer]",
    drawerBody: "[data-cart-drawer-content]",
    main: "[data-sc-main-cart]",
    fragment: "[data-sc-cart-fragment]",
    progress: "[data-sc-shipping-progress]",
    status: "[data-sc-cart-status]",
    error: "[data-sc-cart-error]",
    recommendations: "[data-sc-cart-recommendations]",
  };

  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  const controller = {
    activeElement: null,
    changeTimers: new Map(),
    mutationQueue: Promise.resolve(),
    noteTimer: null,

    init() {
      if (this.initialized) return;
      this.initialized = true;

      // Capture avoids a second POST from the legacy delegated
      // `[data-quick-add]` handler while that block is being retired.
      document.addEventListener("click", this.onClick.bind(this), true);
      document.addEventListener("change", this.onChange.bind(this));
      document.addEventListener("input", this.onInput.bind(this));
      // Código de descuento del pre-carrito (snippets/cyber-carrito-codigo).
      document.addEventListener("submit", (event) => {
        const form = event.target.closest?.("[data-sc-discount-form]");
        if (!form) return;
        event.preventDefault();
        const code = (form.querySelector("input")?.value || "").trim();
        if (code) this.applyDiscount(code, form.querySelector("button"));
      });
      // Cuenta regresiva real del Cyber en el pre-carrito.
      window.setInterval(() => this.tickDrawerCountdown(), 1000);
      // Capture is intentional: application.js still has a legacy Escape
      // listener in bubble phase. Closing here first keeps aria-hidden and
      // focus restoration in sync until that legacy block is removed.
      document.addEventListener("keydown", this.onKeydown.bind(this), true);
      document.addEventListener("shopify:section:load", () =>
        this.loadRecommendations(),
      );

      // Contract with product-page.js: the POST already succeeded. Do not add
      // again; only synchronize the cart UI and reveal the drawer.
      document.addEventListener("sneakclean:cart-added", (event) => {
        const rendered = this.renderSections(event.detail?.item?.sections);
        if (rendered) {
          this.syncFromRenderedDrawer();
          this.open();
          this.setStatus("Producto agregado al carrito.");
        } else {
          this.refresh({ open: true, announce: "Producto agregado al carrito." });
        }
        this.enqueue(() => this.syncPromos());
      });

      this.loadRecommendations();

      // Regalo Cyber: al abrir cualquier página se revisa una vez el carrito
      // (pudo cambiar en otra pestaña, o terminar el Cyber). Solo si el regalo
      // está configurado o ya hay uno en el carrito que quizás deba salir.
      if (window.SC_CONFIG?.gift || window.SC_CONFIG?.giftInCart || window.SC_CONFIG?.protocol || window.SC_CONFIG?.preventa || window.SC_CONFIG?.preventaGiftInCart) {
        this.enqueue(() => this.syncPromos());
      }
    },

    shell() {
      return document.querySelector(SELECTORS.shell);
    },

    route(name, fallback) {
      const shell = this.shell();
      const value = shell?.dataset?.[name];
      if (value) return value;

      const root = window.Shopify?.routes?.root || "/";
      return `${root.replace(/\/?$/, "/")}${fallback.replace(/^\//, "")}`;
    },

    cartUrl() {
      return this.route("cartUrl", "cart");
    },

    cartAddUrl() {
      const route = this.route("cartAddUrl", "cart/add");
      return route.endsWith(".js") ? route : `${route}.js`;
    },

    cartChangeUrl() {
      const route = this.route("cartChangeUrl", "cart/change");
      return route.endsWith(".js") ? route : `${route}.js`;
    },

    cartJsonUrl() {
      return `${this.cartUrl().replace(/\/$/, "")}.js`;
    },

    sectionIds() {
      const ids = [];
      const add = (value) => {
        if (value && !ids.includes(value) && ids.length < 5) ids.push(value);
      };

      add(this.shell()?.dataset.sectionId || "cart-drawer");
      document
        .querySelectorAll(
          `${SELECTORS.main}[data-section-id], ${SELECTORS.fragment}[data-section-id]`,
        )
        .forEach((node) => {
          add(node.dataset.sectionId);
        });
      return ids;
    },

    sectionRequestBody(extra) {
      return {
        ...extra,
        sections: this.sectionIds(),
        sections_url: window.location.pathname,
      };
    },

    onClick(event) {
      const openButton = event.target.closest("[data-open-cart]");
      if (openButton) {
        event.preventDefault();
        this.open(openButton);
        this.refresh({ open: false });
        return;
      }

      if (event.target.closest("[data-sc-cart-close]")) {
        event.preventDefault();
        this.close();
        return;
      }

      const quickAdd = event.target.closest("[data-quick-add]");
      if (quickAdd) {
        event.preventDefault();
        event.stopPropagation();
        // Doble clic: mientras el alta anterior de este botón no termina, se
        // ignora el segundo clic (antes de que setBusy alcance a deshabilitarlo).
        if (quickAdd.classList.contains("is-loading") || quickAdd.disabled) return;
        const variantId = Number.parseInt(quickAdd.dataset.variantId, 10);
        if (!variantId) {
          this.showError("Elige las opciones del producto antes de agregarlo.");
          return;
        }
        this.addRecommendation(variantId, quickAdd);
        return;
      }

      const decrease = event.target.closest("[data-sc-cart-decrease]");
      const increase = event.target.closest("[data-sc-cart-increase]");
      if (decrease || increase) {
        event.preventDefault();
        const wrapper = (decrease || increase).closest(
          "[data-sc-cart-quantity]",
        );
        const input = wrapper?.querySelector("[data-sc-cart-quantity-input]");
        if (!input) return;

        const current = Number.parseInt(input.value, 10) || 0;
        const step = Number.parseInt(input.step, 10) || 1;
        const minimum = Number.parseInt(input.min, 10) || 1;
        const maximum = input.max
          ? Number.parseInt(input.max, 10)
          : Number.POSITIVE_INFINITY;
        input.value = String(
          increase
            ? Math.min(maximum, current + step)
            : Math.max(minimum, current - step),
        );
        this.scheduleLineChange(input, 0);
        return;
      }

      const remove = event.target.closest("[data-sc-cart-remove]");
      if (remove) {
        event.preventDefault();
        const key = remove.dataset.lineKey;
        // Si el cliente quita la Sponge de regalo a mano, el automático no la
        // vuelve a poner en esta sesión: queda el botón «Agregar mi Sponge».
        if (remove.hasAttribute("data-gift-line")) this.setGiftDeclined(true);
        if (key) this.changeLine(key, 0, remove);
        return;
      }

      const discountRemove = event.target.closest("[data-sc-discount-remove]");
      if (discountRemove) {
        event.preventDefault();
        this.applyDiscount("", discountRemove);
        return;
      }

      const addonAdd = event.target.closest("[data-sc-addon-add]");
      if (addonAdd) {
        event.preventDefault();
        event.stopPropagation();
        if (!addonAdd.classList.contains("is-loading")) this.addAddon(addonAdd);
        return;
      }

      const giftAdd = event.target.closest("[data-sc-gift-add]");
      if (giftAdd) {
        event.preventDefault();
        this.setGiftDeclined(false);
        this.addGiftManually(giftAdd);
        return;
      }

      const recommendationButton = event.target.closest(
        "[data-sc-recommendation-add]",
      );
      if (recommendationButton) {
        event.preventDefault();
        const variantId = Number.parseInt(
          recommendationButton.dataset.variantId,
          10,
        );
        if (variantId) this.addRecommendation(variantId, recommendationButton);
      }
    },

    onChange(event) {
      const input = event.target.closest("[data-sc-cart-quantity-input]");
      if (!input) return;
      this.scheduleLineChange(input, 250);
    },

    onInput(event) {
      const note = event.target.closest("[data-sc-cart-note]");
      if (!note) return;
      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(
        () => this.updateNote(note.value),
        500,
      );
    },

    onKeydown(event) {
      if (!this.isOpen()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== "Tab") return;

      const drawer = document.querySelector(SELECTORS.drawer);
      const focusable = Array.from(
        drawer?.querySelectorAll(focusableSelector) || [],
      ).filter((node) => {
        return (
          node.getClientRects().length > 0 &&
          node.getAttribute("aria-hidden") !== "true"
        );
      });
      if (!focusable.length) {
        event.preventDefault();
        drawer?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    scheduleLineChange(input, delay) {
      const key = input.dataset.lineKey;
      if (!key) return;

      const minimum = Number.parseInt(input.min, 10) || 1;
      const maximum = input.max
        ? Number.parseInt(input.max, 10)
        : Number.POSITIVE_INFINITY;
      let quantity = Number.parseInt(input.value, 10);
      if (!Number.isFinite(quantity)) quantity = minimum;
      quantity = Math.max(minimum, Math.min(maximum, quantity));
      input.value = String(quantity);

      window.clearTimeout(this.changeTimers.get(key));
      this.changeTimers.set(
        key,
        window.setTimeout(() => {
          this.changeTimers.delete(key);
          this.changeLine(key, quantity, input);
        }, delay),
      );
    },

    enqueue(operation) {
      this.mutationQueue = this.mutationQueue.catch(() => {}).then(operation);
      return this.mutationQueue;
    },

    serviceUpdates(cart, key, quantity) {
      const line = cart.items.find(item => item.key === key);
      const group = line?.properties?._sc_service_group;
      if (!group || line.properties._sc_service_role !== "base") return null;
      const addons = cart.items.filter(item => item.properties?._sc_service_group === group && item.properties?._sc_service_role === "express-addon");
      if (!addons.length) return null;
      const pairs = Number(line.properties._sc_pairs);
      if (!Number.isInteger(pairs) || pairs < 1) throw new Error("No pudimos identificar los pares de este servicio. Vuelve a agregar el pack.");
      const updates = { [key]: quantity };
      addons.forEach((item, index) => { updates[item.key] = index === 0 ? pairs * quantity : 0; });
      return updates;
    },

    changeLine(key, quantity, trigger) {
      return this.enqueue(async () => {
        this.setBusy(true, trigger);
        this.clearError();
        try {
          const serviceLine = trigger?.closest?.("[data-sc-service-group]");
          const updates = serviceLine ? this.serviceUpdates(await this.fetchCart(), key, quantity) : null;
          const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
          const response = await this.fetchJson(updates ? `${root}cart/update.js` : this.cartChangeUrl(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(
              this.sectionRequestBody(updates ? { updates } : { id: key, quantity }),
            ),
          });

          const sectionsRendered = this.renderSections(response.sections);
          this.updateCartData(response);
          if (!sectionsRendered) await this.refresh({ open: false });
          await this.syncPromos(response);
          this.setStatus(
            quantity === 0
              ? "Producto eliminado del carrito."
              : "Cantidad actualizada.",
          );
        } catch (error) {
          this.showError(
            this.mensajeCliente(
              error,
              "No pudimos actualizar el carrito. Intenta nuevamente.",
            ),
          );
        } finally {
          this.setBusy(false, trigger);
        }
      });
    },

    addRecommendation(variantId, trigger) {
      if (trigger instanceof HTMLElement) trigger.classList.add("is-loading");
      return this.enqueue(async () => {
        this.setBusy(true, trigger);
        this.clearError();
        try {
          const formData = new FormData();
          formData.append("id", String(variantId));
          // La tarjeta de la web blanca trae su propio −/+ (data-quantity).
          const quantity = Number.parseInt(trigger?.dataset?.quantity, 10);
          formData.append("quantity", String(quantity > 0 ? quantity : 1));
          formData.append("sections", this.sectionIds().join(","));
          formData.append("sections_url", window.location.pathname);

          const response = await this.fetchJson(this.cartAddUrl(), {
            method: "POST",
            headers: { Accept: "application/json" },
            body: formData,
          });

          const rendered = this.renderSections(response.sections);
          if (rendered) {
            this.syncFromRenderedDrawer();
          } else {
            await this.refresh({ open: false });
          }
          await this.syncPromos();
          this.open(trigger);
          this.setStatus("Producto agregado al carrito.");
        } catch (error) {
          this.showError(
            this.mensajeCliente(
              error,
              "No pudimos agregar el producto. Intenta nuevamente.",
            ),
          );
        } finally {
          this.setBusy(false, trigger);
        }
      });
    },

    async updateNote(note) {
      try {
        const updateUrl = `${(window.Shopify?.routes?.root || "/").replace(/\/?$/, "/")}cart/update.js`;
        await this.fetchJson(updateUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ note }),
        });
        this.setStatus("Nota guardada.");
      } catch (error) {
        this.showError("No pudimos guardar la nota del pedido.");
      }
    },

    async refresh(options = {}) {
      const { open = false, announce = "" } = options;
      this.setBusy(true);
      this.clearError();
      try {
        const ids = this.sectionIds();
        const requestToken = Date.now();
        const sectionUrl = `${this.cartUrl()}?sections=${encodeURIComponent(ids.join(","))}&_=${requestToken}`;
        const [cart, sections] = await Promise.all([
          this.fetchCart(requestToken),
          // Sin `Accept: application/json`: con ese encabezado, Shopify
          // devuelve el JSON del carro en vez del HTML de la sección.
          this.fetchJson(sectionUrl, { cache: "no-store" }),
        ]);

        this.renderSections(sections);
        this.updateCartData(cart);
        if (open) this.open();
        if (announce) this.setStatus(announce);
      } catch (error) {
        this.showError(
          this.mensajeCliente(
            error,
            "No pudimos cargar el carrito. Intenta nuevamente.",
          ),
        );
        if (open) this.open();
      } finally {
        this.setBusy(false);
      }
    },

    async fetchCart(requestToken = Date.now()) {
      const separator = this.cartJsonUrl().includes("?") ? "&" : "?";
      return this.fetchJson(`${this.cartJsonUrl()}${separator}_=${requestToken}`, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
    },

    async fetchJson(url, options = {}) {
      const response = await fetch(url, {
        credentials: "same-origin",
        ...options,
      });
      const type = response.headers.get("content-type") || "";
      const texto = await response.text();
      let payload = texto;
      // Se intenta parsear siempre que el cuerpo tenga pinta de JSON, no solo
      // cuando el encabezado lo diga: /cart/add.js y /cart/change.js devuelven
      // JSON válido con content-type "text/javascript".
      const pareceJson =
        type.includes("json") ||
        type.includes("javascript") ||
        /^\s*[{[]/.test(texto);
      if (pareceJson) {
        try {
          payload = JSON.parse(texto);
        } catch (_) {
          payload = texto;
        }
      }

      if (!response.ok) {
        const message =
          payload?.description ||
          payload?.message ||
          (typeof payload === "string" ? payload : "Error de Shopify");
        const error = new Error(message);
        // Shopify explica en castellano por qué rechazó ("no queda stock").
        // Eso sí sirve mostrarlo; un fallo de red, no.
        error.deShopify = Boolean(payload?.description || payload?.message);
        throw error;
      }
      return payload;
    },

    renderSections(sections) {
      if (!sections || typeof sections !== "object") return false;
      let rendered = false;

      const drawerSectionId = this.shell()?.dataset.sectionId || "cart-drawer";
      const drawerHtml = sections[drawerSectionId] || sections["cart-drawer"];
      if (drawerHtml) {
        const parsed = this.parse(drawerHtml);
        const freshBody = parsed.querySelector(SELECTORS.drawerBody);
        const currentBody = document.querySelector(SELECTORS.drawerBody);
        if (freshBody && currentBody) {
          currentBody.innerHTML = freshBody.innerHTML;
          rendered = true;
        }
      }

      const currentMain = document.querySelector(SELECTORS.main);
      const mainId = currentMain?.dataset.sectionId;
      if (currentMain && mainId && sections[mainId]) {
        const freshMain = this.parse(sections[mainId]).querySelector(
          SELECTORS.main,
        );
        if (freshMain) {
          currentMain.replaceWith(freshMain);
          rendered = true;
        }
      }

      document
        .querySelectorAll(`${SELECTORS.fragment}[data-section-id]`)
        .forEach((current) => {
          const id = current.dataset.sectionId;
          if (!id || !sections[id]) return;
          const fresh =
            this.parse(sections[id]).querySelector(
              `${SELECTORS.fragment}[data-section-id="${this.escapeSelector(id)}"]`,
            ) || this.parse(sections[id]).querySelector(SELECTORS.fragment);
          if (fresh) {
            current.replaceWith(fresh);
            rendered = true;
          }
        });

      this.loadRecommendations();
      return rendered;
    },

    parse(html) {
      return new DOMParser().parseFromString(html, "text/html");
    },

    escapeSelector(value) {
      return window.CSS?.escape
        ? window.CSS.escape(value)
        : String(value).replace(/["\\]/g, "\\$&");
    },

    updateCartData(cart) {
      if (!cart || typeof cart !== "object") return;
      if (Number.isFinite(cart.item_count)) {
        document.querySelectorAll("[data-cart-count]").forEach((node) => {
          const isDrawerCount = node.classList.contains(
            "sc-cart-drawer__count",
          );
          node.textContent = isDrawerCount
            ? `(${cart.item_count})`
            : String(cart.item_count);
        });
      }
      if (Array.isArray(cart.items)) {
        this.updateShippingProgress(cart);
        // Para las secciones que muestran beneficios con el carrito real.
        document.dispatchEvent(new CustomEvent("sneakclean:cart-updated", { detail: { cart } }));
      }
    },

    syncFromRenderedDrawer() {
      const drawer = document.querySelector(SELECTORS.drawer);
      if (!drawer) return;

      const itemCount = Array.from(
        drawer.querySelectorAll("[data-sc-cart-quantity-input]"),
      ).reduce((sum, input) => sum + (Number.parseInt(input.value, 10) || 0), 0);
      this.updateCartData({ item_count: itemCount });

      const source = drawer.querySelector(SELECTORS.progress);
      if (!source) return;
      document.querySelectorAll(SELECTORS.progress).forEach((target) => {
        if (target === source) return;
        target.dataset.eligibleSubtotal = source.dataset.eligibleSubtotal || "0";
        target.setAttribute(
          "aria-valuenow",
          source.getAttribute("aria-valuenow") || "0",
        );
        const sourceMessage = source.querySelector(
          ".sc-shipping-progress__message",
        );
        const targetMessage = target.querySelector(
          ".sc-shipping-progress__message",
        );
        if (sourceMessage && targetMessage) {
          targetMessage.innerHTML = sourceMessage.innerHTML;
        }
        const sourceFill = source.querySelector(".sc-shipping-progress__fill");
        const targetFill = target.querySelector(".sc-shipping-progress__fill");
        if (sourceFill && targetFill) targetFill.style.width = sourceFill.style.width;
        // Hitos Cyber (regalo / envío): se copia qué hito está alcanzado.
        target.querySelectorAll("[data-milestone]").forEach((milestone) => {
          const twin = source.querySelector(
            `[data-milestone="${milestone.dataset.milestone}"]`,
          );
          if (twin) milestone.classList.toggle("is-reached", twin.classList.contains("is-reached"));
        });
      });

      this.toggleShippingHosts(source.dataset.eligibleSubtotal || 0);
    },

    updateShippingProgress(cart) {
      const eligibleSubtotal = this.eligibleSubtotal(cart);
      const giftSubtotalFor = (node) =>
        this.giftEligibleSubtotal(cart, node.dataset.giftProductId);

      document.querySelectorAll(SELECTORS.progress).forEach((node) => {
        const threshold = Number(node.dataset.threshold) || 0;
        const message = node.querySelector(".sc-shipping-progress__message");
        const fill = node.querySelector(".sc-shipping-progress__fill");
        node.dataset.eligibleSubtotal = String(eligibleSubtotal);

        if (threshold <= 0) {
          if (message) message.textContent = "El despacho se calcula al pagar.";
          if (fill) fill.style.width = "0%";
          return;
        }

        const progress = Math.max(
          0,
          Math.min(100, (eligibleSubtotal / threshold) * 100),
        );
        const remaining = Math.max(0, threshold - eligibleSubtotal);
        node.setAttribute(
          "aria-valuenow",
          String(Math.min(eligibleSubtotal, threshold)),
        );
        if (fill) fill.style.width = `${progress}%`;

        // Cyber: dos hitos (regalo y envío gratis) con el mismo subtotal.
        // Los textos son los de snippets/shipping-progress.liquid.
        const gift = this.giftMilestone(node, threshold);
        const giftSubtotal = giftSubtotalFor(node);
        // Regla de Sergio (29-sep-2026): con código de descuento no hay envío
        // gratis por monto. Si el carrito trae un código, no se promete.
        const conCodigo = (cart.discount_codes || []).some((d) => d.applicable);
        this.renderMilestones(node, gift, conCodigo ? 0 : eligibleSubtotal, threshold, giftSubtotal);

        if (!message) return;
        if (conCodigo) {
          message.innerHTML = "Con código de descuento el envío se paga: <strong>no se suma al envío gratis</strong>.";
          return;
        }
        if (gift) {
          if (eligibleSubtotal >= threshold && giftSubtotal >= gift.threshold) {
            message.textContent = `¡Tu pedido califica para envío gratis y para tu ${gift.label} de regalo!`;
          } else if (eligibleSubtotal >= threshold) {
            message.textContent = "¡Tu pedido califica para envío gratis!";
          } else if (giftSubtotal >= gift.threshold) {
            message.textContent = `Te faltan ${this.formatMoney(remaining)} para el envío gratis.`;
          } else {
            message.textContent = `Te faltan ${this.formatMoney(gift.threshold - giftSubtotal)} para tu ${gift.label} de regalo.`;
          }
        } else if (eligibleSubtotal >= threshold) {
          message.textContent = "¡Tu pedido califica para envío gratis!";
        } else if (eligibleSubtotal > 0) {
          message.textContent = `Te faltan ${this.formatMoney(remaining)} en productos con despacho para obtener envío gratis.`;
        } else {
          message.textContent = `Agrega ${this.formatMoney(threshold)} en productos con despacho para obtener envío gratis.`;
        }
      });

      this.toggleShippingHosts(eligibleSubtotal);
    },

    // Subtotal que cuenta para el regalo y el envío gratis: solo líneas con
    // despacho (los servicios quedan fuera) y nunca la Sponge de regalo.
    eligibleSubtotal(cart) {
      return (cart?.items || []).reduce((sum, item) => {
        if (this.isGiftLine(item)) return sum;
        const requiresShipping =
          item.requires_shipping ?? item.variant?.requires_shipping ?? false;
        return requiresShipping
          ? sum + (Number(item.final_line_price) || 0)
          : sum;
      }, 0);
    },

    // Subtotal que cuenta para el REGALO: el mismo de arriba menos cualquier
    // línea pagada del producto de regalo. El descuento SPONGE FREE exige el
    // monto en la colección regalo-sponge-productos, que excluye la Sponge; si
    // el tema la contara, pondría un regalo que Shopify después cobra.
    giftEligibleSubtotal(cart, giftProductId) {
      const productId = Number(giftProductId) || 0;
      return (cart?.items || []).reduce((sum, item) => {
        if (this.isGiftLine(item)) return sum;
        if (productId && Number(item.product_id) === productId) return sum;
        const requiresShipping =
          item.requires_shipping ?? item.variant?.requires_shipping ?? false;
        return requiresShipping
          ? sum + (Number(item.final_line_price) || 0)
          : sum;
      }, 0);
    },

    // ---------- Código de descuento ----------
    // Shopify aplica el código al carrito (/cart/update.js «discount») y lo
    // lleva al checkout. Si no aplica a este carrito se avisa y se quita.
    applyDiscount(code, trigger) {
      if (trigger instanceof HTMLElement) trigger.setAttribute("aria-busy", "true");
      return this.enqueue(async () => {
        this.setBusy(true, trigger);
        this.clearError();
        const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
        const post = (discount) =>
          this.fetchJson(`${root}cart/update.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ discount }),
          });
        try {
          const cart = await post(code);
          if (code) {
            const found = (cart.discount_codes || []).find(
              (d) => String(d.code).toLowerCase() === code.toLowerCase(),
            );
            if (!found || !found.applicable) {
              await post("");
              await this.refresh({ open: false });
              this.showError(`El código «${code}» no existe o no aplica a este carrito.`);
              return;
            }
          }
          await this.refresh({ open: false });
          await this.syncPromos();
          this.setStatus(code ? `Código ${code} aplicado.` : "Código quitado.");
        } catch (error) {
          this.showError(this.mensajeCliente(error, "No pudimos aplicar el código. Intenta nuevamente."));
        } finally {
          this.setBusy(false, trigger);
          if (trigger instanceof HTMLElement) trigger.removeAttribute("aria-busy");
        }
      });
    },

    tickDrawerCountdown() {
      document.querySelectorAll("[data-sc-drawer-countdown]").forEach((node) => {
        const ends = Date.parse(node.dataset.ends || "");
        const clock = node.querySelector("[data-sc-drawer-clock]");
        const parts = node.querySelectorAll("[data-part]");
        if (!Number.isFinite(ends) || (!clock && !parts.length)) return;
        const left = Math.max(0, ends - Date.now());
        if (!left) {
          node.hidden = true;
          return;
        }
        node.classList.toggle("is-last", left <= 864e5);
        if (parts.length) {
          const v = {
            d: Math.floor(left / 864e5),
            h: Math.floor(left / 36e5) % 24,
            m: Math.floor(left / 6e4) % 60,
            s: Math.floor(left / 1e3) % 60,
          };
          parts.forEach((p) => {
            p.textContent = String(v[p.dataset.part] ?? 0).padStart(2, "0");
          });
          const kicker = node.querySelector(".sc-cy-urgency__kicker");
          if (kicker && left <= 864e5) kicker.textContent = "Último día";
          return;
        }
        const d = Math.floor(left / 864e5);
        const pad = (n) => String(n).padStart(2, "0");
        clock.textContent =
          (d ? d + "d " : "") +
          pad(Math.floor(left / 36e5) % 24) + ":" +
          pad(Math.floor(left / 6e4) % 60) + ":" +
          pad(Math.floor(left / 1e3) % 60);
      });
    },

    // ---------- Complemento Protocol a precio especial ----------
    // Ajustes → Cyber → «Protocol a precio especial» (SC_CONFIG.protocol). El
    // precio lo pone un descuento automático de Shopify; el tema solo ofrece,
    // agrega 1 unidad marcada con _complemento_protocol y la retira si el
    // carrito deja de calificar. Nunca se agrega solo.
    isAddonLine(item) {
      return String(item?.properties?._complemento_protocol ?? "") === "1";
    },

    protocolConfig() {
      const cfg = window.SC_CONFIG?.protocol;
      const variantId = Number(cfg?.variantId) || 0;
      const threshold = Number(cfg?.threshold) || 0;
      if (!variantId || threshold <= 0) return null;
      const now = Date.now();
      const startsAt = Date.parse(cfg.startsAt || "");
      const endsAt = Date.parse(cfg.endsAt || "");
      if (Number.isFinite(startsAt) && now < startsAt) return null;
      if (Number.isFinite(endsAt) && now >= endsAt) return null;
      return { variantId, threshold, productId: Number(cfg.productId) || 0 };
    },

    // Productos con despacho, sin regalo y sin ningún Protocol (el propio
    // complemento no ayuda a llegar al monto).
    addonEligibleSubtotal(cart, productId) {
      return (cart?.items || []).reduce((sum, item) => {
        if (this.isGiftLine(item) || this.isAddonLine(item)) return sum;
        if (productId && Number(item.product_id) === Number(productId)) return sum;
        const requiresShipping =
          item.requires_shipping ?? item.variant?.requires_shipping ?? false;
        return requiresShipping ? sum + (Number(item.final_line_price) || 0) : sum;
      }, 0);
    },

    async syncPromos(knownCart) {
      await this.ensureGift(knownCart);
      await this.ensureAddon();
      await this.ensurePreventa();
    },

    // ---------- Pills de regalo por preventa ----------
    // Ajustes → Preventa (SC_CONFIG.preventa). El regalo es un producto propio
    // a $0, así que no depende de ningún descuento ni choca con SPONGE FREE.
    //   · hay un producto en preventa y no hay regalo → agrega 1 con _regalo_preventa
    //   · regalo repetido o con cantidad ≠ 1          → lo deja en 1 línea de 1
    //   · ya no hay producto en preventa (o se apagó) → lo saca
    // También por variante: los links 🛒 de Jack traen el regalo sin la marca.
    isPreventaGiftLine(item) {
      const gift = Number(window.SC_CONFIG?.preventa?.giftVariantId) || 0;
      return String(item?.properties?._regalo_preventa ?? "") === "1"
        || (gift > 0 && Number(item?.variant_id) === gift);
    },

    async ensurePreventa() {
      const cfg = window.SC_CONFIG?.preventa;
      if (!cfg && !window.SC_CONFIG?.preventaGiftInCart) return false;
      if (this.preventaSyncing) return false;
      this.preventaSyncing = true;
      try {
        const cart = await this.fetchCart();
        const ids = (cfg?.productIds || []).map(Number);
        const hasPack = cart.items.some(
          (item) => !this.isPreventaGiftLine(item) && ids.includes(Number(item.product_id)),
        );
        const lines = cart.items.filter((item) => this.isPreventaGiftLine(item));
        const shouldHave = Boolean(cfg?.giftVariantId) && cfg.giftAvailable !== false && hasPack;
        const updates = {};
        lines.forEach((line, index) => {
          if (!shouldHave || index > 0) updates[line.key] = 0;
          else if (line.quantity !== 1) updates[line.key] = 1;
        });
        const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
        let changed = false;
        if (Object.keys(updates).length) {
          await this.fetchJson(`${root}cart/update.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ updates }),
          });
          changed = true;
        }
        if (shouldHave && !lines.length) {
          await this.fetchJson(this.cartAddUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({
              items: [{ id: Number(cfg.giftVariantId), quantity: 1, properties: { _regalo_preventa: "1" } }],
            }),
          });
          changed = true;
        }
        if (window.SC_CONFIG) window.SC_CONFIG.preventaGiftInCart = shouldHave;
        if (changed) await this.refresh({ open: false });
        return changed;
      } catch (error) {
        console.warn("[SneakClean] No se pudo sincronizar el regalo de preventa:", error);
        return false;
      } finally {
        this.preventaSyncing = false;
      }
    },

    async ensureAddon() {
      if (this.addonSyncing) return false;
      this.addonSyncing = true;
      try {
        const cart = await this.fetchCart();
        const lines = cart.items.filter((item) => this.isAddonLine(item));
        if (!lines.length) return false;
        const cfg = this.protocolConfig();
        const ok = Boolean(cfg) && this.addonEligibleSubtotal(cart, cfg.productId) >= cfg.threshold;
        const updates = {};
        lines.forEach((line, index) => {
          if (!ok || index > 0) updates[line.key] = 0;
          else if (line.quantity !== 1) updates[line.key] = 1;
        });
        if (!Object.keys(updates).length) return false;
        const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
        await this.fetchJson(`${root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates }),
        });
        await this.refresh({ open: false });
        this.setStatus(
          ok
            ? "Protocol a precio especial es 1 por pedido."
            : `Quitamos Protocol a precio especial: es solo para compras de productos desde ${this.formatMoney(cfg?.threshold || 0)}.`,
        );
        return true;
      } catch (error) {
        console.warn("[SneakClean] No se pudo revisar el complemento Protocol:", error);
        return false;
      } finally {
        this.addonSyncing = false;
      }
    },

    addAddon(trigger) {
      if (trigger instanceof HTMLElement) trigger.classList.add("is-loading");
      return this.enqueue(async () => {
        this.setBusy(true, trigger);
        this.clearError();
        try {
          const cfg = this.protocolConfig();
          if (!cfg) throw Object.assign(new Error("La oferta de Protocol no está activa en este momento."), { deShopify: true });
          const cart = await this.fetchCart();
          const falta = cfg.threshold - this.addonEligibleSubtotal(cart, cfg.productId);
          if (falta > 0) {
            throw Object.assign(new Error(`Te faltan ${this.formatMoney(falta)} en productos para sumar Protocol a precio especial.`), { deShopify: true });
          }
          if (!cart.items.some((item) => this.isAddonLine(item))) {
            await this.fetchJson(this.cartAddUrl(), {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({
                items: [{ id: cfg.variantId, quantity: 1, properties: { _complemento_protocol: "1" } }],
              }),
            });
          }
          await this.refresh({ open: false });
          this.open(trigger);
          this.setStatus("Protocol agregado como complemento.");
        } catch (error) {
          this.open(trigger);
          this.showError(this.mensajeCliente(error, "No pudimos agregar Protocol. Intenta nuevamente."));
        } finally {
          this.setBusy(false, trigger);
        }
      });
    },

    giftCodeBlock() {
      try {
        return window.sessionStorage.getItem("sc-regalo-bloqueo-codigo") || "";
      } catch (_error) {
        return "";
      }
    },

    setGiftCodeBlock(value) {
      try {
        if (value) window.sessionStorage.setItem("sc-regalo-bloqueo-codigo", value);
        else window.sessionStorage.removeItem("sc-regalo-bloqueo-codigo");
      } catch (_error) {
        /* sin almacenamiento de sesión */
      }
    },

    // Marca de sesión: el cliente quitó el regalo a mano. sessionStorage puede
    // fallar (modo privado, bloqueos): sin marca, el automático sigue igual.
    giftDeclined() {
      try {
        return window.sessionStorage.getItem("sc-regalo-cyber-quitado") === "1";
      } catch (_error) {
        return false;
      }
    },

    setGiftDeclined(value) {
      try {
        if (value) {
          window.sessionStorage.setItem("sc-regalo-cyber-quitado", "1");
        } else {
          window.sessionStorage.removeItem("sc-regalo-cyber-quitado");
        }
      } catch (_error) {
        /* sin almacenamiento de sesión: no pasa nada */
      }
    },

    // Botón «Agregar mi Sponge gratis». Pasa por ensureGift con la marca de
    // rechazo ya borrada, así se respetan las mismas reglas (umbral, 1 unidad).
    addGiftManually(trigger) {
      return this.enqueue(async () => {
        this.setBusy(true, trigger);
        this.clearError();
        try {
          const changed = await this.ensureGift();
          if (changed) {
            this.setStatus("Regalo agregado al carrito.");
          } else {
            await this.refresh({ open: false });
          }
        } finally {
          this.setBusy(false, trigger);
        }
      });
    },

    isGiftLine(item) {
      return String(item?.properties?._regalo_cyber ?? "") === "1";
    },

    // Configuración del regalo automático (layout/theme.liquid → SC_CONFIG.gift),
    // o null si está apagado o la hora actual cae fuera de las fechas del Cyber.
    giftConfig() {
      const gift = window.SC_CONFIG?.gift;
      const threshold = Number(gift?.threshold) || 0;
      const variantId = Number(gift?.variantId) || 0;
      if (!variantId || threshold <= 0) return null;
      const now = Date.now();
      const startsAt = Date.parse(gift.startsAt || "");
      const endsAt = Date.parse(gift.endsAt || "");
      if (Number.isFinite(startsAt) && now < startsAt) return null;
      if (Number.isFinite(endsAt) && now >= endsAt) return null;
      return { variantId, threshold, productId: Number(gift.productId) || 0 };
    },

    // Regalo Cyber: 1 Sponge por pedido desde el umbral, sin contar servicios.
    // Deja el carrito en el estado correcto y nada más:
    //   · llega al umbral y no hay regalo      → agrega 1 con _regalo_cyber
    //   · hay regalo con cantidad ≠ 1 o repetido → lo deja en 1 línea de 1
    //   · baja del umbral (o terminó el Cyber)  → lo quita
    // El $0 lo pone el descuento automático de Shopify, no el tema.
    // Sin bucles: la bandera giftSyncing corta cualquier llamada mientras se
    // sincroniza, y refresh() no vuelve a llamar a ensureGift. Tras una
    // corrección el carrito ya cumple la regla, así que la siguiente revisión
    // no cambia nada.
    async ensureGift(knownCart) {
      if (this.giftSyncing) return false;
      const gift = this.giftConfig();
      if (!gift && !window.SC_CONFIG?.giftInCart && !knownCart) return false;

      this.giftSyncing = true;
      try {
        const cart =
          knownCart && Array.isArray(knownCart.items)
            ? knownCart
            : await this.fetchCart();
        const giftLines = cart.items.filter((item) => this.isGiftLine(item));
        const reached =
          Boolean(gift) &&
          this.giftEligibleSubtotal(cart, gift.productId) >= gift.threshold;

        // Código de descuento que no se combina con SPONGE FREE: Shopify se
        // queda con el código y el «regalo» se cobra. En ese caso el regalo
        // sale y no vuelve mientras ese código siga en el carrito.
        const codes = (cart.discount_codes || [])
          .filter((d) => d.applicable)
          .map((d) => String(d.code).toLowerCase())
          .sort()
          .join(",");
        let blockedBy = this.giftCodeBlock();
        if (blockedBy && blockedBy !== codes) {
          this.setGiftCodeBlock("");
          blockedBy = "";
        }
        let codeBlocked = false;
        if (reached && codes && giftLines.some((l) => Number(l.final_line_price) > 0)) {
          this.setGiftCodeBlock(codes);
          blockedBy = codes;
          codeBlocked = true;
        }
        const shouldHave = reached && !blockedBy;

        const updates = {};
        let addGift = false;
        if (shouldHave) {
          let kept = false;
          giftLines.forEach((line) => {
            // Solo sirve la variante configurada; cualquier otra sale.
            const valid = Number(line.variant_id) === gift.variantId;
            if (valid && !kept) {
              kept = true;
              if (line.quantity !== 1) updates[line.key] = 1;
            } else {
              updates[line.key] = 0;
            }
          });
          // Si lo quitó a mano en esta sesión, no se vuelve a agregar solo.
          addGift = !kept && !this.giftDeclined();
        } else {
          giftLines.forEach((line) => {
            updates[line.key] = 0;
          });
        }

        // Sponge a precio Cyber: máximo 1 pagada por pedido mientras dure el
        // Cyber (aparte de la de regalo). Si agregan más, queda en 1 y se avisa.
        let spongeLimited = false;
        if (gift) {
          let paidLeft = 1;
          cart.items
            .filter(
              (item) =>
                Number(item.variant_id) === gift.variantId &&
                !this.isGiftLine(item),
            )
            .forEach((line) => {
              const allowed = Math.min(line.quantity, paidLeft);
              paidLeft -= allowed;
              if (allowed !== line.quantity) {
                updates[line.key] = allowed;
                spongeLimited = true;
              }
            });
        }

        const hasUpdates = Object.keys(updates).length > 0;
        if (!hasUpdates && !addGift) {
          if (window.SC_CONFIG) window.SC_CONFIG.giftInCart = giftLines.length > 0;
          return false;
        }

        const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
        if (hasUpdates) {
          await this.fetchJson(`${root}cart/update.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ updates }),
          });
        }
        if (addGift) {
          await this.fetchJson(this.cartAddUrl(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items: [
                {
                  id: gift.variantId,
                  quantity: 1,
                  properties: { _regalo_cyber: "1" },
                },
              ],
            }),
          });
        }
        if (window.SC_CONFIG) window.SC_CONFIG.giftInCart = shouldHave && (addGift || giftLines.length > 0);
        await this.refresh({ open: false });
        if (codeBlocked) {
          this.showError(
            "Tu código no se combina con la caja Sponge de regalo, así que la sacamos para que no se cobre. Si quitas el código, vuelve sola.",
          );
        } else if (spongeLimited) {
          this.setStatus("La Sponge a precio Cyber es 1 por pedido.");
        } else if (gift && !shouldHave && giftLines.length > 0) {
          // Bajó del umbral: el regalo sale y se avisa (el bloque del carrito
          // ya muestra «Te faltan $X para tu caja Sponge de regalo»).
          this.setStatus(
            `Tu compra de productos bajó de ${this.formatMoney(gift.threshold)}: el regalo salió del carrito.`,
          );
        }
        return true;
      } catch (error) {
        // Sin stock del regalo u otro rechazo: no se reintenta en bucle; el
        // pedido sigue su curso sin regalo y queda el aviso en consola.
        console.warn("[SneakClean] No se pudo sincronizar el regalo Cyber:", error);
        return false;
      } finally {
        this.giftSyncing = false;
      }
    },

    // Hito del regalo Cyber. Solo existe si el tema lo configuró (producto y
    // umbral en Ajustes → Cyber), si es menor que el de envío y si la hora
    // actual cae dentro de las fechas cargadas. Mismo chequeo que
    // snippets/cyber-regalo-activo.liquid, repetido acá porque la página puede
    // quedar abierta mientras empieza o termina el Cyber.
    giftMilestone(node, threshold) {
      const giftThreshold = Number(node.dataset.giftThreshold) || 0;
      if (giftThreshold <= 0 || giftThreshold >= threshold) return null;
      const now = Date.now();
      const startsAt = Date.parse(node.dataset.giftStartsAt || "");
      const endsAt = Date.parse(node.dataset.giftEndsAt || "");
      if (Number.isFinite(startsAt) && now < startsAt) return null;
      if (Number.isFinite(endsAt) && now >= endsAt) return null;
      return {
        threshold: giftThreshold,
        label: node.dataset.giftLabel || "regalo",
      };
    },

    renderMilestones(node, gift, eligibleSubtotal, threshold, giftSubtotal = eligibleSubtotal) {
      const marker = node.querySelector("[data-gift-marker]");
      const list = node.querySelector("[data-milestones]");
      node.classList.toggle("sc-shipping-progress--hitos", Boolean(gift));
      const note = node.querySelector("[data-progress-note]");
      if (note) {
        note.textContent = gift
          ? "Los servicios no suman para el regalo ni para el envío gratis."
          : "Los servicios no se consideran para el mínimo de despacho.";
      }
      if (marker) {
        marker.hidden = !gift;
        if (gift) marker.style.left = `${(gift.threshold / threshold) * 100}%`;
      }
      if (!list) return;
      list.hidden = !gift;
      const giftItem = list.querySelector('[data-milestone="gift"]');
      const shippingItem = list.querySelector('[data-milestone="shipping"]');
      if (giftItem) giftItem.classList.toggle("is-reached", Boolean(gift) && giftSubtotal >= gift.threshold);
      if (shippingItem) shippingItem.classList.toggle("is-reached", eligibleSubtotal >= threshold);
    },

    // La barra de envío del header solo aparece cuando hay algo con despacho en
    // el carrito. Vacía repetía el mensaje de la barra de anuncios y ocupaba el
    // primer pantallazo en celular. El nodo siempre existe en el DOM para que
    // updateShippingProgress pueda refrescarlo antes de mostrarlo.
    toggleShippingHosts(eligibleSubtotal) {
      document.querySelectorAll("[data-sc-shipping-host]").forEach((host) => {
        host.hidden = !(Number(eligibleSubtotal) > 0);
      });
    },

    formatMoney(cents) {
      const currency = window.SC_CONFIG?.currency || "CLP";
      try {
        return new Intl.NumberFormat("es-CL", {
          style: "currency",
          currency,
          maximumFractionDigits: currency === "CLP" ? 0 : 2,
        }).format(Number(cents) / 100);
      } catch (_error) {
        return `$${Math.round(Number(cents) / 100).toLocaleString("es-CL")}`;
      }
    },

    open(trigger) {
      const drawer = document.querySelector(SELECTORS.drawer);
      const overlay = document.querySelector(
        "[data-sc-cart-close].sc-cart-drawer__overlay",
      );
      if (!drawer) return;

      if (!this.isOpen()) {
        this.activeElement =
          trigger ||
          (document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null);
      }
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      overlay?.classList.add("is-open");
      document.querySelectorAll("[data-open-cart]").forEach((button) => {
        button.setAttribute("aria-controls", drawer.id || "CartDrawer");
        button.setAttribute("aria-expanded", "true");
      });
      document.documentElement.classList.add("is-locked");
      window.requestAnimationFrame(() =>
        drawer.querySelector(".sc-cart-drawer__close")?.focus(),
      );
    },

    close() {
      const drawer = document.querySelector(SELECTORS.drawer);
      const overlay = document.querySelector(
        "[data-sc-cart-close].sc-cart-drawer__overlay",
      );
      if (!drawer) return;

      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      overlay?.classList.remove("is-open");
      document.querySelectorAll("[data-open-cart]").forEach((button) => {
        button.setAttribute("aria-expanded", "false");
      });
      document.documentElement.classList.remove("is-locked");
      const returnTarget = this.activeElement?.isConnected
        ? this.activeElement
        : document.querySelector("[data-open-cart]");
      returnTarget?.focus?.({ preventScroll: true });
      this.activeElement = null;
    },

    isOpen() {
      return (
        document
          .querySelector(SELECTORS.drawer)
          ?.classList.contains("is-open") || false
      );
    },

    // Al cliente solo le llega un mensaje que pueda entender y accionar. Un
    // "Failed to fetch" o un "NetworkError" no le dice nada y da desconfianza
    // justo en el momento de comprar.
    mensajeCliente(error, porDefecto) {
      return error?.deShopify && error.message ? error.message : porDefecto;
    },

    setBusy(busy, trigger) {
      document
        .querySelectorAll(`${SELECTORS.drawerBody}, [data-sc-cart-items]`)
        .forEach((node) => {
          node.setAttribute("aria-busy", String(busy));
        });
      document
        .querySelectorAll(
          "[data-sc-cart-decrease], [data-sc-cart-increase], [data-sc-cart-remove], [data-sc-cart-quantity-input], [data-sc-recommendation-add], [data-quick-add]",
        )
        .forEach((control) => {
          // Los «Agregar» de la página no se apagan todos: parecía que un clic
          // apretaba los dos del banner. Solo se apaga el tocado; las altas
          // igual van en fila (enqueue).
          if (control.matches("[data-quick-add]") && control !== trigger && !control.dataset.scBusyDisabled) return;
          if (busy && !control.disabled) {
            control.disabled = true;
            control.dataset.scBusyDisabled = "true";
          } else if (!busy && control.dataset.scBusyDisabled === "true") {
            control.disabled = false;
            delete control.dataset.scBusyDisabled;
          }
        });
      document
        .querySelectorAll(SELECTORS.shell)
        .forEach((node) => node.classList.toggle("is-loading", busy));
      if (trigger instanceof HTMLElement)
        trigger.classList.toggle("is-loading", busy);
    },

    setStatus(message) {
      document.querySelectorAll(SELECTORS.status).forEach((node) => {
        node.textContent = "";
        window.requestAnimationFrame(() => {
          node.textContent = message;
        });
      });
    },

    clearError() {
      document.querySelectorAll(SELECTORS.error).forEach((node) => {
        node.hidden = true;
        node.textContent = "";
      });
    },

    showError(message) {
      document.querySelectorAll(SELECTORS.error).forEach((node) => {
        node.textContent = message;
        node.hidden = false;
      });
      this.setStatus(message);
    },

    async loadRecommendations() {
      const containers = document.querySelectorAll(SELECTORS.recommendations);
      await Promise.all(
        Array.from(containers).map((container) =>
          this.loadRecommendationContainer(container),
        ),
      );
    },

    async loadRecommendationContainer(container) {
      const productId = container.dataset.productId;
      const list = container.querySelector(
        "[data-sc-cart-recommendations-list]",
      );
      if (
        !productId ||
        !list ||
        container.dataset.loading === "true" ||
        container.dataset.loadedFor === productId
      )
        return;

      container.dataset.loading = "true";
      const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
      const endpoint = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=4&intent=complementary`;

      try {
        const payload = await this.fetchJson(endpoint, {
          headers: { Accept: "application/json" },
        });
        const inCart = new Set(
          Array.from(
            document.querySelectorAll("[data-sc-cart-line][data-product-id]"),
          ).map((node) => String(node.dataset.productId)),
        );
        const products = (
          Array.isArray(payload?.products) ? payload.products : []
        )
          .filter(
            (product) => product?.available && !inCart.has(String(product.id)),
          )
          .slice(0, 4);

        list.replaceChildren();
        if (!products.length) {
          container.hidden = true;
          return;
        }

        products.forEach((product) =>
          list.appendChild(this.recommendationCard(product)),
        );
        container.hidden = false;
        container.dataset.loadedFor = productId;
      } catch (_error) {
        // Recommendations are optional; a failure must never block checkout.
        container.hidden = true;
      } finally {
        delete container.dataset.loading;
      }
    },

    recommendationCard(product) {
      const card = document.createElement("article");
      card.className = "cart-rec sc-cart-rec";

      const media = document.createElement("a");
      media.className = "cart-rec__media";
      media.href = this.safeProductUrl(product.url);
      media.setAttribute("aria-label", `Ver ${product.title || "producto"}`);
      const imageSource = product.featured_image || product.images?.[0];
      if (imageSource) {
        const image = document.createElement("img");
        image.src = this.safeImageUrl(imageSource);
        image.alt = product.title || "";
        image.loading = "lazy";
        media.appendChild(image);
      }

      const info = document.createElement("div");
      info.className = "cart-rec__info";
      const title = document.createElement("a");
      title.className = "cart-rec__title";
      title.href = this.safeProductUrl(product.url);
      title.textContent = product.title || "Producto";
      const price = document.createElement("p");
      price.className = "cart-rec__price";
      price.textContent = this.formatMoney(product.price || 0);
      info.append(title, price);

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const canQuickAdd = variants.length === 1 && variants[0]?.available;
      let action;
      if (canQuickAdd) {
        action = document.createElement("button");
        action.type = "button";
        action.className = "cart-rec__add";
        action.dataset.scRecommendationAdd = "";
        action.dataset.variantId = String(variants[0].id);
        action.setAttribute(
          "aria-label",
          `Agregar ${product.title || "producto"} al carrito`,
        );
        action.textContent = "+";
      } else {
        action = document.createElement("a");
        action.className = "sc-cart-rec__options";
        action.href = this.safeProductUrl(product.url);
        action.textContent = "Elegir";
        action.setAttribute(
          "aria-label",
          `Elegir opciones de ${product.title || "producto"}`,
        );
      }

      card.append(media, info, action);
      return card;
    },

    safeProductUrl(value) {
      try {
        const url = new URL(
          value || this.route("rootUrl", "/"),
          window.location.origin,
        );
        return url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : this.route("rootUrl", "/");
      } catch (_error) {
        return this.route("rootUrl", "/");
      }
    },

    safeImageUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        return ["http:", "https:"].includes(url.protocol) ? url.href : "";
      } catch (_error) {
        return "";
      }
    },
  };

  window.SneakCleanCartController = controller;
  window.SneakCleanCart = {
    refresh: (options) => controller.refresh(options),
    open: (trigger) => controller.open(trigger),
    close: () => controller.close(),
  };
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        controller.init();
        document.documentElement.dataset.cartController = "ready";
      },
      { once: true },
    );
  } else {
    controller.init();
    document.documentElement.dataset.cartController = "ready";
  }
})();
